/**
 * 72.3 step 7 (ARCHITECTURE §5.35 "The bots' posts"): a building coming down is no place to be. While its collapse runs
 * (StructureState.collapse, GameConfig.collapse), its footprint and COLLAPSE.clearPeople round it are a hazard: the cover
 * producers (core/coverMap.ts, core/coverSlots.ts) offer no post there, a bot's plan lets go a destination there, and a body
 * standing there walks out by the nearest side (ai/actionBot.ts). Apart from core/collapse.ts so the cover code needs no
 * damage imports. Pure; the collapsing list is memoised on the structures array.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { COLLAPSE } from '../constants/modules';
import { layoutExtent, layoutFor } from './modules';
import { structureFrame } from './hits';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub } from '../math';

type HazardState = Pick<GameState, 'structures' | 'planets' | 'config'>;

const collapsingOf = new WeakMap<readonly StructureState[], readonly StructureState[]>();
/** The structures coming down (none with the switch off). */
function collapsing(state: HazardState): readonly StructureState[] {
  if (!state.config.collapse) return [];
  let out = collapsingOf.get(state.structures);
  if (!out) collapsingOf.set(state.structures, (out = state.structures.filter(s => s.collapse && s.health > 0)));
  return out;
}

const boxes = new WeakMap<StructureState, { up: Vec3; tx: Vec3; tz: Vec3; hx: number; hz: number } | null>();
/** A collapsing building's hazard box: its frame and half widths (people), its footprint and the clearance (kept per structure object). */
function boxOf(s: StructureState, config: HazardState['config']) {
  let box = boxes.get(s);
  if (box === undefined) {
    const layout = layoutFor(s, config);
    if (!layout) box = null;
    else {
      const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up), ext = layoutExtent(layout);
      box = { up, tx, tz, hx: ext.halfX + COLLAPSE.clearPeople, hz: ext.halfZ + COLLAPSE.clearPeople };
    }
    boxes.set(s, box);
  }
  return box;
}

/**
 * The collapsing building whose footprint (and COLLAPSE.clearPeople round it) `dir` lies in on planet `planetIndex`, and the
 * way out: a unit direction just past the nearer side. Null when none (or the switch off).
 */
export function collapseHazardAt(state: HazardState, planetIndex: number, dir: Vec3): { id: number; out: Vec3 } | null {
  const list = collapsing(state);
  if (list.length === 0) return null;
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = planet.radius, n = vecNormalize(dir);
  for (const s of list) {
    if (s.planetIndex !== planetIndex) continue;
    const box = boxOf(s, state.config);
    if (!box) continue;
    const { up, tx, tz, hx, hz } = box;
    const off = vecScale(vecSub(n, up), R / person), px = vecDot(off, tx), pz = vecDot(off, tz);   // (people, along the ground)
    if (Math.abs(px) > hx || Math.abs(pz) > hz) continue;
    const sx = px >= 0 ? 1 : -1, sz = pz >= 0 ? 1 : -1, alongX = hx - Math.abs(px) <= hz - Math.abs(pz);
    const ox = alongX ? sx * (hx + 0.5) : px, oz = alongX ? pz : sz * (hz + 0.5);
    return { id: s.id, out: vecNormalize(vecAdd(up, vecAdd(vecScale(tx, (ox * person) / R), vecScale(tz, (oz * person) / R)))) };
  }
  return null;
}

/** `points` without those in a collapsing building (the same array when none is coming down). */
export function clearOfCollapse<T extends { dir: Vec3 }>(state: HazardState, planetIndex: number, points: T[]): T[] {
  if (collapsing(state).length === 0) return points;
  return points.filter(p => !collapseHazardAt(state, planetIndex, p.dir));
}
